import { dataFieldTypes } from '@dashboard/services/pageFieldTypes/dataFieldTypes';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import getBillingModel, { BILLING_MODELS } from '@dashboard/services/getBillingModel';
import { htmlToPlainText } from '@dashboard/lib/htmlToPlainText';

// Unified billing-variable catalog (replaces the old paymentVariables /
// subscriptionVariables split that keyed off the legacy scalar product.type).
// Each entry's `applies` says which billing types resolve it:
//   'both'      — resolves for one-time AND recurring purchases
//   'oneTime'   — resolves only for a one-time (charge) purchase
//   'recurring' — resolves only for a subscription / payment-plan purchase
// The menu shows the set matching the page's billingModel. `confirmationOnly`
// entries are hidden in reminder emails (which carry a reduced variable set).
//
// Labels are neutral (Amount / Created date / Status) in every billing mode —
// the old per-type wording ("Payment amount" / "Subscription amount") is gone
// (Matt, 2026-06-17). Order is chosen so a charge page reproduces the previous
// payment-variable order exactly.
const BILLING_VARIABLES = [
  { id: 'price', label: 'Amount', applies: 'both' },
  { id: 'amountDue', label: 'Payment amount due', applies: 'oneTime', confirmationOnly: true },
  { id: 'amountPaid', label: 'Payment amount paid', applies: 'oneTime', confirmationOnly: true },
  {
    id: 'currentPeriodEnd',
    label: 'Next payment date',
    applies: 'recurring',
    confirmationOnly: true,
  },
  { id: 'planIterations', label: 'No. of payments', applies: 'recurring' },
  { id: 'createdAt', label: 'Created date', applies: 'both' },
  { id: 'status', label: 'Status', applies: 'both', confirmationOnly: true },
  { id: 'payment_method', label: 'Payment method', applies: 'both', confirmationOnly: true },
  { id: 'orderId', label: 'Order ID', applies: 'both' },
];

const ONE_TIME_HINT = 'Only applies to one-time purchases';
const RECURRING_HINT = 'Only applies to subscription purchases';

/**
 * The billing variables offered for a given billingModel, as `{ name, id }`
 * (the shape RichTextarea inserts). On a mixed page both type-specific sets are
 * shown — each resolves only for the matching purchase, so they carry a `hint`
 * clarifying which (one template still serves every price). Single-billing pages
 * only show their own set, so no hint is needed.
 */
function getBillingVariables(billingModel, reminderEmail) {
  const hasOneTime =
    billingModel === BILLING_MODELS.CHARGE || billingModel === BILLING_MODELS.MIXED;
  const hasRecurring =
    billingModel === BILLING_MODELS.SUBSCRIPTION || billingModel === BILLING_MODELS.MIXED;
  const isMixed = billingModel === BILLING_MODELS.MIXED;

  return BILLING_VARIABLES.filter(variable => {
    if (reminderEmail && variable.confirmationOnly) return false;
    if (variable.applies === 'oneTime') return hasOneTime;
    if (variable.applies === 'recurring') return hasRecurring;
    return true;
  }).map(variable => {
    if (isMixed && variable.applies === 'oneTime') {
      return { name: variable.label, id: variable.id, hint: ONE_TIME_HINT };
    }
    if (isMixed && variable.applies === 'recurring') {
      return { name: variable.label, id: variable.id, hint: RECURRING_HINT };
    }
    return { name: variable.label, id: variable.id };
  });
}

/**
 * Build the merge-variable list offered by the confirmation / reminder message
 * editor for a page. Pure (no React) so it can be unit-tested directly.
 *
 * Billing variables are gated on the page's billing composition (billingModel),
 * not the legacy scalar product.type — so a mixed-billing multi-price page
 * exposes both one-time and subscription variables. The stored
 * `product.billingModel` is read directly (single source of truth, computed on
 * save + backfilled by migration); it falls back to a live computation for any
 * product not yet carrying the field.
 */
export function buildEmailVariables({ page, reminderEmail = false, confirmationEmail = false }) {
  if (!page) {
    return [];
  }

  // Rendered from the purchase's order, which only a confirmation email has.
  const orderSummaryVariables = confirmationEmail
    ? [{ name: 'Order summary', id: 'order_summary' }]
    : [];

  const productVariables = [
    { name: 'Product title', id: 'product_title' },
    ...orderSummaryVariables,
    { name: 'Variant selection', id: 'variant_breakdown' },
    ...(page.product?.variants ?? [])
      .map(variant => [
        {
          name: htmlToPlainText(variant.name),
          id: `variant_${variant._id}`,
        },
        ...(variant.options.some(option => option.sku)
          ? [
              {
                name: `${htmlToPlainText(variant.name)} SKU`,
                id: `variant_${variant._id}_sku`,
              },
            ]
          : []),
      ])
      .flat(),
    ...(page.product?.generateLicenseKeys ? [{ name: 'License key', id: 'license_key' }] : []),
    { name: 'SKU', id: 'sku' },
  ];

  const submissionVariables = [
    { name: 'Form title', id: 'form_title' },
    { name: 'Submission date', id: 'createdAt' },
  ];

  const eventVariables = [
    { name: 'Event name', id: 'event_name' },
    { name: 'Event date/time', id: 'event_date' },
    { name: 'Event location', id: 'event_location' },
    { name: 'Tickets purchased', id: 'event_tickets' },
    ...orderSummaryVariables,
    { name: 'Booking fee', id: 'booking_fee' },
    {
      name: 'Virtual meeting link',
      id: 'event_meeting_link',
    },
  ];

  const genericVariables = [
    ...(reminderEmail === false
      ? [{ name: 'Customer portal link', id: 'customer_portal_link' }]
      : []),
    ...(page.fields ?? []).map(field => {
      if (!field.name) {
        return {
          name: field.label,
          id: `field_${field._id}`,
        };
      }

      const dataField = dataFieldTypes.find(f => f.name === field.type);

      return {
        name: dataField?.label || field.label,
        id: `field_${field._id}`,
      };
    }),
    ...(page.showCouponCodeField
      ? [
          { name: 'Coupon code', id: 'coupon_code' },
          { name: 'Coupon discount', id: 'coupon_discount' },
        ]
      : []),
  ];

  if (page.type === PAGE_TYPES.FORM) {
    return [...submissionVariables, ...genericVariables];
  }

  if (page.type === PAGE_TYPES.CHECKOUT) {
    // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (getBillingModel falls back to product.type)
    const billingModel = page.product?.billingModel ?? getBillingModel(page.product);
    return [
      ...getBillingVariables(billingModel, reminderEmail),
      ...productVariables,
      ...genericVariables,
    ];
  }

  if (page.type === PAGE_TYPES.EVENT) {
    // Events sell one-time tickets — use the charge billing set. Event variables
    // are a separate page-type axis (not billing-mix driven), kept as-is.
    return [
      ...getBillingVariables(BILLING_MODELS.CHARGE, reminderEmail),
      ...eventVariables,
      ...genericVariables,
    ];
  }

  return [];
}
